import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone, Mail, Calendar, DollarSign, MessageSquare, Save, Loader2, X, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "quoted", label: "Quoted" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
  { value: "archived", label: "Archived" },
];

export default function LeadDetailDialog({ open, onOpenChange, lead, token, onSaved }) {
  const [form, setForm] = useState({
    status: "new",
    notes: "",
    quote_amount: "",
    next_action_date: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && lead) {
      setForm({
        status: lead.status || "new",
        notes: lead.notes || "",
        quote_amount: lead.quote_amount != null ? String(lead.quote_amount) : "",
        next_action_date: lead.next_action_date || "",
      });
    }
  }, [open, lead]);

  if (!lead) return null;

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const payload = {
        status: form.status,
        notes: form.notes,
        quote_amount: form.quote_amount === "" ? null : Number(form.quote_amount),
        next_action_date: form.next_action_date || null,
      };
      const { data } = await axios.patch(`${API}/admin/leads/${lead.id}`, payload, {
        headers: { "X-Admin-Token": token },
      });
      toast.success("Lead saved.");
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-md" data-testid="lead-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-navy text-2xl">{lead.name}</DialogTitle>
          <DialogDescription>
            Submitted {formatDate(lead.created_at)} · Source:{" "}
            <span className="font-medium">{lead.source || "website-form"}</span>
          </DialogDescription>
        </DialogHeader>

        {/* Contact strip */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-secondary rounded-md p-4">
          <a href={`tel:${lead.phone}`} className="flex items-center gap-2 text-sm hover:text-sunrise">
            <Phone className="w-4 h-4 text-sunrise" /> {lead.phone}
          </a>
          <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-sm hover:text-sunrise break-all">
            <Mail className="w-4 h-4 text-sunrise" /> {lead.email}
          </a>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="font-medium text-navy">Service:</span> {lead.service_type}
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground capitalize">
            <span className="font-medium text-navy">Project:</span> {lead.project_type}
          </div>
        </div>

        {/* Original message */}
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-navy mb-2">Visitor message</div>
          <div className="bg-white border border-border rounded-md p-3 text-sm leading-relaxed whitespace-pre-line max-h-40 overflow-y-auto">
            {lead.message}
          </div>
        </div>

        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-navy flex items-center gap-1">
                <MessageSquare className="w-3 h-3" /> Status
              </span>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger className="mt-1 rounded-md" data-testid="lead-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-navy flex items-center gap-1">
                <DollarSign className="w-3 h-3" /> Quote amount
              </span>
              <Input
                data-testid="lead-quote-input"
                type="number"
                step="0.01"
                value={form.quote_amount}
                onChange={(e) => setForm((f) => ({ ...f, quote_amount: e.target.value }))}
                className="mt-1 rounded-md"
                placeholder="0.00"
              />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-navy flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Next action
              </span>
              <Input
                data-testid="lead-next-action-input"
                type="date"
                value={form.next_action_date}
                onChange={(e) => setForm((f) => ({ ...f, next_action_date: e.target.value }))}
                className="mt-1 rounded-md"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-navy">Follow-up notes</span>
            <Textarea
              data-testid="lead-notes-input"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={5}
              className="mt-1 rounded-md"
              placeholder="Site visit scheduled for Tuesday, quoted $18k for kitchen remodel, customer wants to compare with one other contractor before deciding…"
            />
          </label>

          <DialogFooter className="gap-2 pt-2 flex-wrap">
            <a
              href={`mailto:${lead.email}`}
              className="text-xs text-muted-foreground hover:text-sunrise inline-flex items-center gap-1 mr-auto"
            >
              <ExternalLink className="w-3 h-3" /> Open in email client
            </a>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-md">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button type="submit" disabled={saving} className="bg-sunrise hover:bg-sunrise-hover rounded-md" data-testid="lead-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}
